import {
  groupDigits,
  normalizeDigits,
  PERSIAN_DECIMAL_SEPARATOR,
  toPersianDigits,
} from './digits.js';

/**
 * Money rules (FTD §3.1, AC §4.1, D-05, F-3).
 *
 * - API amounts are integer rials (`*Irr`). They are displayed in toman (rial / 10) with `تومان`.
 * - A rial amount that is not a whole toman is shown exactly with one toman decimal. Nothing here
 *   rounds: one rial is exactly 0.1 toman, so every rial amount has an exact one-decimal form.
 * - Compaction truncates toward zero and is for dashboards only, never for payable amounts.
 * - Input is typed in toman and converted to integer rials at the API boundary.
 *
 * All arithmetic is integer arithmetic on safe integers; no floating-point division is used.
 */

/** Integer rials. The API's only money unit. */
export type Rials = number;

export const TOMAN_LABEL = 'تومان';
export const RIAL_LABEL = 'ریال';

/** Left-to-right mark plus U+2212 MINUS SIGN, the CLDR `fa` negative prefix. */
const NEGATIVE_PREFIX = '‎−';

function assertRials(rials: number): void {
  if (!Number.isSafeInteger(rials)) {
    throw new RangeError('Money amounts must be safe integers in rials.');
  }
}

/** Splits a rial amount into its sign, whole toman, and the remaining rial (0–9). */
function splitToman(rials: Rials): { negative: boolean; toman: number; tenth: number } {
  assertRials(rials);

  const absolute = Math.abs(rials);
  const tenth = absolute % 10;

  return { negative: rials < 0, tenth, toman: (absolute - tenth) / 10 };
}

export interface MoneyFormatOptions {
  /** Appends ` تومان` (or ` ریال`). Defaults to true. */
  readonly label?: boolean;
}

/**
 * Formats integer rials as toman with Persian digits and grouping:
 * `1234560` → `۱۲۳٬۴۵۶ تومان`, `266667` → `۲۶٬۶۶۶٫۷ تومان`.
 */
export function formatToman(rials: Rials, options: MoneyFormatOptions = {}): string {
  const { negative, tenth, toman } = splitToman(rials);
  const whole = groupDigits(String(toman));
  const decimal = tenth === 0 ? '' : `${PERSIAN_DECIMAL_SEPARATOR}${toPersianDigits(tenth)}`;
  const label = options.label === false ? '' : ` ${TOMAN_LABEL}`;

  return `${negative ? NEGATIVE_PREFIX : ''}${whole}${decimal}${label}`;
}

/** Exact rial display for admin audit, ledger, and reconciliation views (FTD §3.1). */
export function formatRials(rials: Rials, options: MoneyFormatOptions = {}): string {
  assertRials(rials);

  const label = options.label === false ? '' : ` ${RIAL_LABEL}`;

  return `${rials < 0 ? NEGATIVE_PREFIX : ''}${groupDigits(String(Math.abs(rials)))}${label}`;
}

/** True when the amount is a whole number of toman (a multiple of 10 rials). */
export function isWholeToman(rials: Rials): boolean {
  assertRials(rials);

  return rials % 10 === 0;
}

const COMPACT_UNITS = [
  // Rials per unit: 1 billion toman, 1 million toman, 1 thousand toman.
  { labelFa: 'میلیارد', rialsPerUnit: 10_000_000_000 },
  { labelFa: 'میلیون', rialsPerUnit: 10_000_000 },
  { labelFa: 'هزار', rialsPerUnit: 10_000 },
] as const;

/**
 * Dashboard compaction, for example `123456789` rials → `۱۲٫۳ میلیون تومان`. One decimal,
 * **truncated toward zero** (never rounded up). Never use it for payable amounts, confirmation
 * screens, or receipts; use {@link formatToman} there.
 */
export function formatTomanCompact(rials: Rials, options: MoneyFormatOptions = {}): string {
  assertRials(rials);

  const absolute = Math.abs(rials);
  const unit = COMPACT_UNITS.find((candidate) => absolute >= candidate.rialsPerUnit);

  if (unit === undefined) {
    // Below one thousand toman the exact value is short enough.
    return formatToman(rials, options);
  }

  const tenthStep = unit.rialsPerUnit / 10;
  // Exact integer truncation: remove the remainder before dividing.
  const tenths = (absolute - (absolute % tenthStep)) / tenthStep;
  const whole = Math.trunc(tenths / 10);
  const tenth = tenths % 10;
  const decimal = tenth === 0 ? '' : `${PERSIAN_DECIMAL_SEPARATOR}${toPersianDigits(tenth)}`;
  const label = options.label === false ? '' : ` ${TOMAN_LABEL}`;
  const sign = rials < 0 && tenths > 0 ? NEGATIVE_PREFIX : '';

  return `${sign}${groupDigits(String(whole))}${decimal} ${unit.labelFa}${label}`;
}

export type MoneyInputError = 'EMPTY' | 'INVALID' | 'TOO_MANY_DECIMALS' | 'TOO_LARGE';

export type MoneyInputResult =
  | { readonly ok: true; readonly rials: Rials }
  | { readonly ok: false; readonly error: MoneyInputError };

/** Grouping characters accepted in typed amounts: ASCII comma, Persian separator, Arabic comma, spaces. */
const GROUPING = /[,٬،\s‌‏‎]/g;

/**
 * Parses a toman amount typed by a person (Persian, Arabic-Indic, or Latin digits, optional grouping,
 * at most one decimal digit) into integer rials. `'۱۲۳٬۴۵۶٫۷'` → `1234567`. Negative amounts are not
 * accepted as input.
 */
export function parseTomanInput(input: string): MoneyInputResult {
  const normalized = normalizeDigits(input).replace(GROUPING, '').replace(/[٫]/g, '.');

  if (normalized === '') {
    return { error: 'EMPTY', ok: false };
  }

  const match = /^(\d+)(?:\.(\d*))?$/.exec(normalized);

  if (match === null) {
    return { error: 'INVALID', ok: false };
  }

  const [, whole = '', fraction = ''] = match;
  const trimmedFraction = fraction.replace(/0+$/, '');

  if (trimmedFraction.length > 1) {
    return { error: 'TOO_MANY_DECIMALS', ok: false };
  }

  const digits = `${whole}${trimmedFraction.padEnd(1, '0')}`.replace(/^0+(?=\d)/, '');

  // 16 digits can exceed 2^53 - 1; compare as strings first so nothing is silently rounded.
  if (digits.length > 16 || !Number.isSafeInteger(Number(digits))) {
    return { error: 'TOO_LARGE', ok: false };
  }

  return { ok: true, rials: Number(digits) };
}

/** The value a money input shows for an existing rial amount: grouped toman without the label. */
export function rialsToTomanInput(rials: Rials): string {
  return formatToman(rials, { label: false });
}
